import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import DateBar, { today, daysAgo } from "../components/DateBar";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";

const fmt = (v) =>
  "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtP = (v) => Number(v).toFixed(1) + "%";
const PIE_COLORS = ["var(--green)", "var(--blue)", "var(--amber)", "var(--purple)"];
const PM_STYLE = {
  Wallet: { bg: "var(--green-light)", color: "var(--green-dark)" },
  "Online Payment": { bg: "var(--blue-light)", color: "var(--blue-dark)" },
  Cash: { bg: "var(--badge-amber-bg)", color: "var(--badge-amber-text)" },
  "Bank Deposit": { bg: "var(--badge-tan-bg)", color: "var(--badge-tan-text)" },
};

function PayKPIRow({ paymode }) {
  const totRev = paymode.reduce((s, r) => s + r.revenue, 0);
  const totCGST = paymode.reduce((s, r) => s + (r.cgst || 0), 0);
  const totSGST = paymode.reduce((s, r) => s + (r.sgst || 0), 0);
  const totIGST = paymode.reduce((s, r) => s + (r.igst || 0), 0);
  const totTax = totCGST + totSGST + totIGST;
  const totBills = paymode.reduce((s, r) => s + r.bill_count, 0);

  const items = [
    { label: "Total Collection", value: fmt(totRev), color: "var(--green)" },
    {
      label: "Total Bills",
      value: totBills.toLocaleString("en-IN"),
      color: "var(--blue)",
    },
    { label: "Total Tax", value: fmt(totTax), color: "var(--red)" },
    { label: "Total CGST", value: fmt(totCGST), color: "var(--teal)" },
    { label: "Total SGST", value: fmt(totSGST), color: "var(--violet)" },
    { label: "Total IGST", value: fmt(totIGST), color: "var(--orange)" },
    {
      label: "Tax %",
      value: fmtP(totRev ? (totTax / totRev) * 100 : 0),
      color: "var(--orange2)",
    },
  ];
  return (
    <div className="summary-row">
      {items.map((c) => (
        <div
          key={c.label}
          className="summary-card"
          style={{ borderTopColor: c.color }}
        >
          <div className="summary-label">{c.label}</div>
          <div className="summary-value">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function PaymentReport({ showToast }) {
  const [preset, setPreset] = useState(1);
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(today);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("Summary");
  const [paymode, setPaymode] = useState([]);
  const [taxData, setTaxData] = useState([]);

  const TABS = [
    { key: "Summary", icon: "📊" },
    { key: "Tax Breakdown", icon: "🧮" },
    // { key: 'By Mode',       icon: '💳' },
    { key: "Daily Mode", icon: "📅" },
  ];

  const fetchAll = useCallback(
    async (s, e) => {
      setLoading(true);
      try {
        const [pm, td] = await Promise.all([
          api.paymode(s, e),
          api.paymentReport(s, e),
        ]);
        setPaymode(pm);
        setTaxData(td);
      } catch {
        showToast("Failed to fetch payment data", "error");
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    fetchAll(start, end);
  }, [start, end, fetchAll]);

  const totCGST = taxData.reduce((s, d) => s + d.cgst, 0);
  const totSGST = taxData.reduce((s, d) => s + d.sgst, 0);
  const totIGST = taxData.reduce((s, d) => s + d.igst, 0);
  const totTax = taxData.reduce((s, d) => s + d.total_tax, 0);
  const totRev = taxData.reduce((s, d) => s + d.revenue, 0);
  const totBase = taxData.reduce((s, d) => s + d.basic_amount, 0);
  const totBills = taxData.reduce((s, d) => s + d.total_bills, 0);

  return (
    <div className="page-body">
      <div className="page-title">Payment Report</div>
      <div className="page-subtitle">
        Tax collection — CGST, SGST, IGST — and payment mode breakdown
      </div>

      <DateBar
        start={start}
        end={end}
        preset={preset}
        setStart={setStart}
        setEnd={setEnd}
        setPreset={setPreset}
        loading={loading}
      />

      {paymode.length > 0 && <PayKPIRow paymode={paymode} />}

      {/* Tabs */}
      <div className="tabs-wrap">
        <div className="tabs-inner">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`tab-btn ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.key}
            </button>
          ))}
        </div>
      </div>

      {/* ── Summary tab ──────────────────────────── */}
      {tab === "Summary" && (
        <div className="grid-2">
          {/* Donut pie */}
          <div className="card">
            <div className="card-title">Revenue by payment mode</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              {paymode.map((m, i) => (
                <span
                  key={m.paymode}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "12px",
                    color: "var(--muted)",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: PIE_COLORS[i % PIE_COLORS.length],
                      flexShrink: 0,
                    }}
                  />
                  {m.paymode} {m.percentage}%
                </span>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={paymode}
                  dataKey="revenue"
                  nameKey="paymode"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={42}
                >
                  {paymode.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Mode summary table */}
          <div className="card">
            <div className="card-title">Payment mode summary</div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Mode</th>
                    <th style={{ textAlign: "right" }}>Revenue</th>
                    <th style={{ textAlign: "center" }}>Bills</th>
                    <th style={{ textAlign: "right" }}>CGST</th>
                    <th style={{ textAlign: "right" }}>SGST</th>
                    <th style={{ textAlign: "right" }}>IGST</th>
                    <th style={{ textAlign: "right" }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {paymode.map((m) => {
                    const s = PM_STYLE[m.paymode] || {
                      bg: "var(--badge-neutral-bg)",
                      color: "var(--badge-neutral-text)",
                    };
                    return (
                      <tr key={m.paymode}>
                        <td>
                          <span
                            className="badge"
                            style={{ background: s.bg, color: s.color }}
                          >
                            {m.paymode}
                          </span>
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontWeight: 600,
                            color: "var(--green)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmt(m.revenue)}
                        </td>
                        <td style={{ textAlign: "center" }}>{m.bill_count}</td>
                        <td
                          style={{ textAlign: "right", whiteSpace: "nowrap" }}
                        >
                          {fmt(m.cgst || 0)}
                        </td>
                        <td
                          style={{ textAlign: "right", whiteSpace: "nowrap" }}
                        >
                          {fmt(m.sgst || 0)}
                        </td>
                        <td
                          style={{ textAlign: "right", whiteSpace: "nowrap" }}
                        >
                          {fmt(m.igst || 0)}
                        </td>
                        <td style={{ textAlign: "right" }}>{m.percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--green)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(paymode.reduce((s, m) => s + m.revenue, 0))}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {paymode.reduce((s, m) => s + m.bill_count, 0)}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {fmt(paymode.reduce((s, m) => s + (m.cgst || 0), 0))}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {fmt(paymode.reduce((s, m) => s + (m.sgst || 0), 0))}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {fmt(paymode.reduce((s, m) => s + (m.igst || 0), 0))}
                    </td>
                    <td style={{ textAlign: "right" }}>100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tax Breakdown tab ────────────────────── */}
      {tab === "Tax Breakdown" && (
        <>
          <div className="card">
            <div className="card-title">
              CGST / SGST / IGST daily stacked trend
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={taxData}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d.slice(5).replace("-", "/")}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  tickFormatter={(v) => "₹" + (v / 1000).toFixed(0) + "k"}
                  tick={{ fontSize: 10 }}
                  width={52}
                />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="cgst" fill="var(--teal)" name="CGST" stackId="tax" />
                <Bar dataKey="sgst" fill="var(--violet)" name="SGST" stackId="tax" />
                <Bar
                  dataKey="igst"
                  fill="var(--orange)"
                  name="IGST"
                  stackId="tax"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <div className="card-title">Daily tax details</div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th style={{ textAlign: "center" }}>Bills</th>
                    <th style={{ textAlign: "right" }}>Basic Amt</th>
                    <th style={{ textAlign: "right" }}>CGST</th>
                    <th style={{ textAlign: "right" }}>SGST</th>
                    <th style={{ textAlign: "right" }}>IGST</th>
                    <th style={{ textAlign: "right" }}>Total Tax</th>
                    <th style={{ textAlign: "right" }}>Total Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {taxData.map((d) => (
                    <tr key={d.date}>
                      <td style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                        {d.date}
                      </td>
                      <td style={{ textAlign: "center" }}>{d.total_bills}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {fmt(d.basic_amount)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "var(--teal)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(d.cgst)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "var(--violet)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(d.sgst)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "var(--orange)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(d.igst)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: 600,
                          color: "var(--red)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(d.total_tax)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: 600,
                          color: "var(--green)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(d.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td style={{ textAlign: "center" }}>{totBills}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {fmt(totBase)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--teal)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(totCGST)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--violet)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(totSGST)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--orange)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(totIGST)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--red)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(totTax)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--green)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(totRev)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── By Mode tab ──────────────────────────── */}
      {tab === "By Mode" && (
        <div className="card">
          <div className="card-title">
            Revenue, CGST, SGST, IGST by payment mode
          </div>
          <ResponsiveContainer
            width="100%"
            height={Math.max(220, paymode.length * 70)}
          >
            <BarChart
              data={paymode}
              layout="vertical"
              margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--grid)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tickFormatter={(v) => "₹" + (v / 100000).toFixed(1) + "L"}
                tick={{ fontSize: 10 }}
              />
              <YAxis
                type="category"
                dataKey="paymode"
                tick={{ fontSize: 12 }}
                width={120}
              />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar
                dataKey="revenue"
                fill="var(--green)"
                name="Revenue"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="cgst"
                fill="var(--teal)"
                name="CGST"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="sgst"
                fill="var(--violet)"
                name="SGST"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="igst"
                fill="var(--orange)"
                name="IGST"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Daily Mode tab ───────────────────────── */}
      {tab === "Daily Mode" && (
        <div className="card">
          <div className="card-title">
            Daily total collection — basic amount + taxes stacked
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={taxData}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5).replace("-", "/")}
                tick={{ fontSize: 10 }}
              />
              <YAxis
                tickFormatter={(v) => "₹" + (v / 1000).toFixed(0) + "k"}
                tick={{ fontSize: 10 }}
                width={52}
              />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar
                dataKey="basic_amount"
                fill="var(--green)"
                name="Basic Amt"
                stackId="a"
              />
              <Bar dataKey="cgst" fill="var(--teal)" name="CGST" stackId="a" />
              <Bar dataKey="sgst" fill="var(--violet)" name="SGST" stackId="a" />
              <Bar
                dataKey="igst"
                fill="var(--orange)"
                name="IGST"
                stackId="a"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
